import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isSameDay } from "date-fns";
import {
  ExternalLink,
  RefreshCw,
  Database,
  Loader2,
  Music,
  Calendar,
  Pencil,
  Trash2,
  Plus,
  X,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Save,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  AlertCircle,
  Ban,
  EyeOff,
  RotateCcw,
  GitMerge,
} from "lucide-react";
import { Button, buttonVariants } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { PreferredLabel } from "../../components/ui/PreferredLabel";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { TableColumnHeader } from "../../components/ui/TableColumnHeader";
import { MobileFilterBar, chip } from "../../components/ui/MobileFilterBar";
import { Badge } from "../../components/ui/Badge";
import { ClassificationProfileDetail } from "../../components/ui/ClassificationProfileDetail";
import { DocumentTranslations } from "../translations/DocumentTranslations";
import { EventStatusBadge, EVENT_STATUS_META } from "../../components/ui/EventStatusBadge";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { CitySelect } from "../../components/ui/CitySelect";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { EntitySearchPicker } from "../../components/ui/EntitySearchPicker";
import { OnlyMarkDuplicateToggle } from "../../components/ui/DuplicateReview";
import ImageListEditor from "../../components/ui/ImageListEditor";
import { BillingRankInput } from "../../components/ui/BillingRankInput";
import { EntityLink } from "../../components/ui/EntityLink";
import {
  CLASSIFIED_FILTER_OPTIONS,
  ClassificationIssueBadge,
  classifiedFilterParams,
  ClassifyAction,
  useClassify,
} from "../../components/ui/ClassifyAction";
import { artistsApi, eventsApi, venuesApi, imageReviewApi } from "../../utils/api";
import { cn } from "../../utils/cn";
import { useEntityDeepLink } from "../../hooks/useEntityDeepLink";
import { useSuspiciousImages } from "../../hooks/useSuspiciousImages";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import DuplicatesView from "./DuplicatesView";
import ImageReviewView from "./ImageReviewView";
import EventDisplayImage from "./EventDisplayImage";
import { Link } from "react-router-dom";
import KeepCancelledToggle from "./KeepCancelledToggle";
import {
  changedOccurrenceTimes,
  hasShowSiblings,
  initialKeepCancelled,
  keepCancelledPayload,
  cancellationSource,
  withStatus,
} from "./occurrenceTimes";
import { apiErrorMessage, parseCommaSeparated, parseWholeNumberInput, arraysEqual, applyUrlRefUrlChange, capitalize, definedEntries, displayLabel, formatDate, getAdminEntityUrl, hasClassificationProfile, getEventWebUrl, normalizeImageRefs as normalizeEventImages, parseRankInput, rankIsUnusable, rankLabel, reconcileImageUrls, selectBestImageUrl as selectBestImage, SOURCE_LABELS, sourceLabel } from "../../utils/helpers";

// Open an event on the consumer web app. No-op when the web base URL can't be
// resolved (localhost dev without VITE_WEB_URL), so links never point at a bad host.
const openOnApplauss = (eventId) => {
  const url = getEventWebUrl(eventId);
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
// `cancelled` is derived by the API (every occurrence cancelled, not merged away) and shown as
// `display_status`; a fully cancelled event matches no other status.
const EVENT_STATUS_OPTIONS = ["active", "pending", "hidden", "cancelled", "duplicate"];
const DEFAULT_STATUS_FILTER = ["active", "pending", "hidden", "cancelled"];
// Which feed produced the event. Server-side and single-select: the API takes one `source`.
const SOURCE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  ...Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label })),
];
const SERVER_SORT_KEYS = new Set(["date", "first_seen", "date_promoted"]);
const normalizeStatusFilter = (statuses) => EVENT_STATUS_OPTIONS.filter((status) => statuses.includes(status));
const EVENT_STATUS_FILTER_OPTIONS = EVENT_STATUS_OPTIONS.map((status) => ({
  value: status,
  label: EVENT_STATUS_META[status]?.label || capitalize(status),
}));

const normalizeUrlRefs = (urls = []) =>
  (urls || [])
    .map((entry) => (typeof entry === "string" ? { provider: "", url: entry } : entry || {}))
    .map((entry) => ({
      provider: entry.provider || "",
      url: entry.url || "",
      // Preserve the "bad" suppression marker through map/diff/payload (only when true), with
      // `bad_by` when it was set automatically, so a save does not turn it into an admin's.
      ...(entry.bad === true ? { bad: true, ...(entry.bad_by ? { bad_by: entry.bad_by } : {}) } : {}),
    }))
    .filter((entry) => entry.url);

// Read-only views of stored refs. Keeps the API's `first_seen_at` and `public_visible`, which
// normalizeUrlRefs (the edit/save shape, and what the save diff compares) deliberately drops.
const displayUrlRefs = (urls = []) =>
  (urls || []).map((entry) => (typeof entry === "string" ? { url: entry } : entry || {})).filter((entry) => entry.url);

// When ingestion first saw an occurrence or link (#599), and whether the public event detail
// shows it — the API computes that mark with the detail's own filters, so it is never guessed here.
function SeenMeta({ item }) {
  if (!item.first_seen_at && !item.public_visible) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-[10px] text-muted-foreground">
      {item.first_seen_at && <span title="First seen by ingestion">seen {formatDate(item.first_seen_at)}</span>}
      {item.public_visible && (
        <Badge variant="success" className="text-[10px] py-0" title="Shown on the public event detail">
          Public
        </Badge>
      )}
    </span>
  );
}

// Density presets for UrlRefRow. `sm` is the compact >20-occurrences editor; `md`/`lg` match the
// per-occurrence and event-level editors respectively.
const URL_ROW_SIZES = {
  sm: { gap: "gap-1", grid: "sm:grid-cols-[7rem_minmax(0,1fr)_1.75rem_1.75rem]", input: "h-7 rounded border px-2 text-xs focus:ring-2", btn: "h-7 w-7", icon: "h-3.5 w-3.5" },
  md: { gap: "gap-2", grid: "sm:grid-cols-[10rem_minmax(0,1fr)_2.25rem_2.25rem]", input: "h-8 rounded-md border px-3 text-sm focus:ring-2", btn: "h-8 w-8", icon: "h-4 w-4" },
  lg: { gap: "gap-2", grid: "sm:grid-cols-[10rem_minmax(0,1fr)_2.25rem_2.25rem]", input: "h-9 rounded-md border px-3 text-sm focus:ring-2", btn: "h-9 w-9", icon: "h-4 w-4" },
};

// One editable ticket-URL row: provider + url inputs, a Mark-bad/Restore toggle, and a link that
// opens the URL. There is no remove: the next fetch would re-add a deleted URL, so Mark-bad is how
// one is retired (#235). Shared by all three URL editors in this file (event-level, per-occurrence, and the
// >20-occurrences OccurrencesEditor) so the mark-bad control can't be added to one and forgotten
// in another. Callers own the state; `urlRefRowHandlers` below builds the change handlers so
// every editor shares one set of mutation rules.
// `bad_by` of a link the Ticketmaster check hid because the search stopped listing it (the API's
// `UNLISTED_BAD_BY`); a re-fetch that serves it again clears it, unlike an admin's mark.
const UNLISTED_BAD_BY = "provider_unlisted";

export function UrlRefRow({ urlRef, size = "md", onProviderChange, onUrlChange, onToggleBad }) {
  const s = URL_ROW_SIZES[size] || URL_ROW_SIZES.md;
  // Only absolute http(s) URLs get an href: a half-typed "ticketmaster.com/..." would open
  // relative to the admin host, and a provider-supplied "javascript:" URL would run here.
  const href = /^https?:\/\//i.test(urlRef.url || "") ? urlRef.url : undefined;
  const inputBase = "border border-input bg-background focus:outline-none focus:ring-ring";
  return (
    <div className={cn("grid grid-cols-1", s.gap, s.grid)}>
      <input
        type="text"
        value={urlRef.provider || ""}
        onChange={(e) => onProviderChange(e.target.value)}
        placeholder="Provider"
        className={cn(s.input, inputBase)}
      />
      <input
        type="url"
        value={urlRef.url || ""}
        onChange={(e) => onUrlChange(e.target.value)}
        placeholder="https://..."
        className={cn(s.input, inputBase, "min-w-0", urlRef.bad && "line-through text-muted-foreground opacity-70")}
      />
      <Button
        type="button"
        variant={urlRef.bad ? "secondary" : "ghost"}
        size="icon"
        className={s.btn}
        onClick={onToggleBad}
        title={
          urlRef.bad
            ? urlRef.bad_by === UNLISTED_BAD_BY
              ? "Hidden automatically: Ticketmaster stopped listing it. Restore URL (unmark bad)"
              : "Restore URL (unmark bad)"
            : "Mark URL bad (hide from consumers)"
        }
      >
        {urlRef.bad ? <RotateCcw className={s.icon} /> : <Ban className={s.icon} />}
      </Button>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title="Open URL"
        className={cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          s.btn,
          !href && "pointer-events-none opacity-50"
        )}
      >
        <ExternalLink className={s.icon} />
      </a>
    </div>
  );
}

// Builds the three UrlRefRow handlers for one row of a ticket-URL list. All three editors keep
// their URLs in a plain array and differ only in how the updated array is committed, so the
// row-mutation rules — including the pasted-URL provider auto-fill — live here once instead of
// being re-typed (and drifting) per editor.
const urlRefRowHandlers = (urls, index, commit) => {
  const replace = (next) => commit(urls.map((row, i) => (i === index ? next : row)));
  return {
    onProviderChange: (value) => replace({ ...urls[index], provider: value, _providerAuto: false }),
    onUrlChange: (value) => replace(applyUrlRefUrlChange(urls[index], value)),
    // A toggle is the admin's own call, so it drops any automatic `bad_by`.
    onToggleBad: () => replace({ ...urls[index], bad: !urls[index].bad, bad_by: undefined }),
  };
};

const linksObjectToRows = (links = {}) =>
  Object.entries(links || {}).map(([key, value]) => ({
    _key: `${key}-${value}`,
    key,
    value: value || "",
  }));

const linkRowsToObject = (rows = []) => {
  const result = {};
  rows.forEach((row) => {
    const key = row.key?.trim();
    const value = row.value?.trim();
    if (key && value) result[key] = value;
  });
  return result;
};

export const mapApiEventToDisplayFormat = (event) => {
  const nextOcc = event.occurrences?.[0];
  const rawTime = nextOcc?.local_time;
  const displayImage = normalizeEventImages(event.display_image ? [event.display_image] : [])[0];

  return {
    id: event.id,
    title: event.title || "",
    preferredTitle: event.preferred_title || "",
    description: event.description || "",
    // Agent-written and admin-accepted; consumers show it INSTEAD of `description`. Editable
    // here so a bad one can be fixed or cleared without going back to the research record.
    aiDescription: event.ai_description || "",
    urls: normalizeUrlRefs(event.urls || []),
    displayUrls: displayUrlRefs(event.urls),
    links: event.links || {},
    image: displayImage?.url || selectBestImage(event.images),
    imageResolutionStatus: event.image_resolution_status || null,
    images: normalizeEventImages(event.images),
    primaryImageUrl: event.primary_image_url || null,
    mobileImageUrl: event.mobile_image_url || null,
    artist: capitalize(event.artists?.[0]?.name || "Artist TBA"),
    artistsData: event.artists || [],
    hiddenArtists: event.hidden_artists || [],
    date: nextOcc?.local_date || "",
    time: rawTime ? rawTime.substring(0, 5) : "",
    venue: capitalize(event.venue?.name || event.venue_name || "Venue TBA"),
    venueId: event.venue?.id || event.venue_id || null,
    venueCity: event.venue?.city || "",
    venueSize: event.venue?.size || "",
    venueType: Array.isArray(event.venue?.type)
      ? event.venue.type.join(", ")
      : event.venue?.type || "",
    venueSetting: event.venue?.setting || "",
    venueAddress: event.venue?.address || "",
    mainPopulation: event.main_population || "",
    domain: event.domain || [],
    themes: event.themes || [],
    types: event.types || [],
    languages: event.languages || [],
    occurrences: event.occurrences || [],
    // No `source` field: an event carries one `source_ids` key per feed that produced it.
    sources: definedEntries(event.source_ids).map(([provider]) => provider),
    status: event.status || "active",
    // What the badge shows: `cancelled` when every occurrence is (derived by the API, never stored).
    display_status: event.display_status || null,
    duplicateOf: event.duplicate_of || null,
    startPrice: event.start_price || null,
    // Run time and youngest admitted age (#792): a provider's, event research's or an admin's.
    durationMinutes: event.duration_minutes ?? null,
    minAge: event.min_age ?? null,
    enrichmentMetadata: event.enrichment_metadata || null,
    classificationProfile: event.classification_profile || null,
    // An event classified on its own curates genres (#754), stored beside its profile.
    curatedGenres: event.curated_genres || [],
    first_seen: event.first_seen || null,
    date_promoted: event.date_promoted || null,
    _raw: event,
  };
};

// The artist an event's classification was copied from, when it was — the shape the API refreshes
// from the artist (`classification_source: "artist"` and a single artist). The editor points those
// at the artist; every other event's classification is read-only for now.
export const inheritedClassificationArtist = (event) => {
  if ((event?.classificationProfile?.classification_source || "") !== "artist") return null;
  // Every ref counts, id or not — `followed_artist_id` in the API: a second ref, even one without
  // an id, means the sync no longer refreshes the event from the first.
  const artists = event?.artistsData || [];
  return artists.length === 1 && artists[0]?.id ? artists[0] : null;
};


// --- Occurrence status config ---
const OCC_STATUS = {
  onsale:      { dot: "bg-green-500",     badge: "success",     label: "On Sale" },
  on_sale:     { dot: "bg-green-500",     badge: "success",     label: "On Sale" },
  sold_out:    { dot: "bg-red-500",       badge: "destructive", label: "Sold Out" },
  soldout:     { dot: "bg-red-500",       badge: "destructive", label: "Sold Out" },
  cancelled:   { dot: "bg-destructive",   badge: "destructive", label: "Cancelled" },
  canceled:    { dot: "bg-destructive",   badge: "destructive", label: "Cancelled" },
  postponed:   { dot: "bg-yellow-500",    badge: "warning",     label: "Postponed" },
  rescheduled: { dot: "bg-blue-500",      badge: "secondary",   label: "Rescheduled" },
  presale:     { dot: "bg-purple-500",    badge: "secondary",   label: "Presale" },
};

const formatOccTime = (timeString) => {
  if (!timeString) return null;
  return timeString.substring(0, 5);
};

const getDayDotColor = (dayOccs) => {
  if (!dayOccs || dayOccs.length === 0) return "bg-primary";
  const statuses = dayOccs.map((o) => o.status?.toLowerCase());
  if (statuses.some((s) => s === "on_sale" || s === "onsale")) return OCC_STATUS.on_sale.dot;
  if (statuses.some((s) => s === "presale")) return OCC_STATUS.presale.dot;
  if (statuses.some((s) => s === "rescheduled")) return OCC_STATUS.rescheduled.dot;
  if (statuses.some((s) => s === "postponed")) return OCC_STATUS.postponed.dot;
  if (statuses.some((s) => s === "sold_out" || s === "soldout")) return OCC_STATUS.sold_out.dot;
  return "bg-muted-foreground";
};

// --- Shared Calendar Grid ---
function CalendarGrid({ occurrences = [], allowSelectEmpty = false, renderDayDetail }) {
  const initialMonth = useMemo(() => {
    if (occurrences.length === 0) return new Date();
    const sorted = [...occurrences].sort((a, b) => new Date(a.local_date) - new Date(b.local_date));
    return startOfMonth(new Date(sorted[0].local_date));
  }, [occurrences]);

  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setCurrentMonth(initialMonth);
  }, [initialMonth]);

  const occurrencesByDate = useMemo(() => {
    const map = {};
    occurrences.forEach((occ) => {
      const dateKey = format(new Date(occ.local_date), "yyyy-MM-dd");
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(occ);
    });
    Object.values(map).forEach((dayOccs) => {
      dayOccs.sort((a, b) => (a.local_time || "").localeCompare(b.local_time || ""));
    });
    return map;
  }, [occurrences]);

  const selectedDayOccurrences = useMemo(() => {
    if (!selectedDay) return [];
    const dateKey = format(selectedDay, "yyyy-MM-dd");
    return occurrencesByDate[dateKey] || [];
  }, [selectedDay, occurrencesByDate]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const monthOccurrenceCount = useMemo(() => {
    let count = 0;
    calendarDays.forEach((day) => {
      if (isSameMonth(day, currentMonth)) {
        const dateKey = format(day, "yyyy-MM-dd");
        if (occurrencesByDate[dateKey]) count++;
      }
    });
    return count;
  }, [calendarDays, currentMonth, occurrencesByDate]);

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handlePrevMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)); setSelectedDay(null); };
  const handleNextMonth = () => { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)); setSelectedDay(null); };

  const handleDayClick = (day) => {
    const dateKey = format(day, "yyyy-MM-dd");
    const hasOccs = !!occurrencesByDate[dateKey];
    if (hasOccs || allowSelectEmpty) {
      setSelectedDay((prev) => (prev && isSameDay(prev, day) ? null : day));
    }
  };

  return (
    <div className="flex gap-4">
      <div className="shrink-0">
        <div className="border border-border rounded-lg p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <button onClick={handlePrevMonth} className="p-1 hover:bg-muted rounded transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-center">
              <span className="text-sm font-semibold">{format(currentMonth, "MMM yyyy")}</span>
              {monthOccurrenceCount > 0 && (
                <span className="text-xs text-muted-foreground ml-1.5">({monthOccurrenceCount})</span>
              )}
            </div>
            <button onClick={handleNextMonth} className="p-1 hover:bg-muted rounded transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7">
            {weekDays.map((d) => (
              <div key={d} className="text-center text-[10px] font-semibold text-muted-foreground py-1">{d.charAt(0)}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const dateKey = format(day, "yyyy-MM-dd");
              const dayOccs = occurrencesByDate[dateKey] || [];
              const hasOccurrences = dayOccs.length > 0;
              const isSelected = selectedDay && isSameDay(day, selectedDay);
              const isToday = isSameDay(day, new Date());
              const clickable = hasOccurrences || allowSelectEmpty;

              return (
                <button
                  key={index}
                  onClick={() => isCurrentMonth && handleDayClick(day)}
                  disabled={!isCurrentMonth || !clickable}
                  className={`
                    h-8 flex flex-col items-center justify-center text-xs font-medium rounded transition-colors
                    ${!isCurrentMonth ? "text-muted-foreground/20" : hasOccurrences ? "text-foreground cursor-pointer" : allowSelectEmpty ? "text-muted-foreground cursor-pointer" : "text-muted-foreground/40"}
                    ${isSelected ? "bg-primary/15 ring-1 ring-primary" : ""}
                    ${!isSelected && clickable && isCurrentMonth ? "hover:bg-muted" : ""}
                  `}
                >
                  <span className={`
                    w-6 h-6 flex items-center justify-center rounded-full text-xs leading-none
                    ${isSelected ? "bg-primary text-primary-foreground font-bold" : ""}
                    ${isToday && !isSelected ? "ring-1 ring-foreground/30" : ""}
                  `}>
                    {format(day, "d")}
                  </span>
                  {hasOccurrences && isCurrentMonth && !isSelected && (
                    <span className={`w-1 h-1 rounded-full ${getDayDotColor(dayOccs)} -mt-0.5`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        {renderDayDetail({ selectedDay, dayOccurrences: selectedDayOccurrences })}
      </div>
    </div>
  );
}

// --- Read-only calendar ---
function OccurrencesCalendar({ occurrences = [] }) {
  if (occurrences.length === 0) return null;

  return (
    <CalendarGrid
      occurrences={occurrences}
      renderDayDetail={({ selectedDay, dayOccurrences }) =>
        selectedDay ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">{format(selectedDay, "EEEE, MMMM d")}</h4>
              <span className="text-xs text-muted-foreground">
                {dayOccurrences.length} {dayOccurrences.length === 1 ? "show" : "shows"}
              </span>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {dayOccurrences.map((occ, i) => {
                const statusKey = occ.status?.toLowerCase();
                return (
                  <div key={occ.id || `${occ.local_date}-${occ.local_time}-${i}`} className="space-y-1.5 p-2 rounded-md bg-muted/50 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="shrink-0">{formatOccTime(occ.local_time) || "TBA"}h</span>
                        {occ.name && (
                          <span className="min-w-0 truncate text-muted-foreground">
                            {occ.name}
                          </span>
                        )}
                      </div>
                      <SeenMeta item={occ} />
                      {occ.is_valid === false && (
                        <Badge variant="destructive" className="text-[10px] py-0">Invalid</Badge>
                      )}
                      <Badge variant={OCC_STATUS[statusKey]?.badge || "secondary"} className="text-[10px] py-0">
                        {OCC_STATUS[statusKey]?.label || occ.status || "Available"}
                      </Badge>
                    </div>
                    {displayUrlRefs(occ.urls).map((urlRef, urlIndex) => (
                      <div key={`${urlRef.url}-${urlIndex}`} className="ml-5 flex min-w-0 items-center gap-2">
                        <a
                          href={urlRef.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "min-w-0 truncate text-xs hover:underline",
                            urlRef.bad ? "text-muted-foreground line-through" : "text-blue-600"
                          )}
                          title={urlRef.bad ? "Marked bad — hidden from consumers" : undefined}
                        >
                          {urlRef.bad ? "bad · " : ""}{(urlRef.provider || "url")}: {urlRef.url}
                        </a>
                        <SeenMeta item={urlRef} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Click a highlighted day to see its shows
          </div>
        )
      }
    />
  );
}

// A blank occurrence row for the edit modal. `is_valid: null` is "not judged yet", which the
// checkbox and the API both read as valid.
const makeEmptyOccurrence = (date = "") => ({
  _key: `new-${Date.now()}`,
  id: null,
  name: "",
  local_date: date,
  local_time: "",
  status: "onsale",
  keep_cancelled: true,
  urls: [],
  is_valid: null,
});

// --- Doors and set times of one date (#792) ---
// Set times are keyed on the event's artist refs; a time whose artist is not on the line-up any
// more still shows (by id) so it can be removed.
const setTimeArtistLabel = (artists, artistId) => {
  const artist = artists.find((a) => a.id === artistId);
  return artist ? displayLabel(artist.preferred_name, artist.name) : artistId;
};

function OccurrenceTimes({ occ, artists }) {
  const setTimes = occ.set_times || [];
  if (!occ.doors_time && setTimes.length === 0) return null;
  return (
    <p className="ml-5 text-xs text-muted-foreground">
      {occ.doors_time && <span>Doors {occ.doors_time}</span>}
      {occ.doors_time && setTimes.length > 0 && " · "}
      {setTimes.map((t) => `${setTimeArtistLabel(artists, t.artist_id)} ${t.time}`).join(" · ")}
    </p>
  );
}

// Fixes or clears what research, a provider or an earlier edit stored: a blank doors time or set
// time clears it on save (`changedOccurrenceTimes` sends only what changed).
export function OccurrenceTimesEditor({ occ, artists, hasSiblings = false, onChange }) {
  const setTimes = occ.set_times || [];
  const inputClass =
    "h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <label className="flex items-center gap-1">
        Doors
        <input
          type="time"
          value={occ.doors_time || ""}
          onChange={(e) => onChange({ doors_time: e.target.value })}
          className={inputClass}
        />
      </label>
      {setTimes.map((t, i) => (
        <span key={t.artist_id} className="flex items-center gap-1">
          {setTimeArtistLabel(artists, t.artist_id)}
          <input
            type="time"
            value={t.time}
            onChange={(e) =>
              onChange({ set_times: setTimes.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)) })
            }
            className={inputClass}
          />
          <button
            type="button"
            aria-label="Remove set time"
            onClick={() => onChange({ set_times: setTimes.filter((_, j) => j !== i) })}
            className="hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {hasSiblings && (
        // Another tier of this show shares its date and start: by default a time edit here reaches the
        // tiers holding the same old value too; untick to give this tier a value of its own.
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={occ.timesApplyToShow !== false}
            onChange={(e) => onChange({ timesApplyToShow: e.target.checked })}
          />
          Apply to every ticket tier of this show
        </label>
      )}
    </div>
  );
}

// --- Editable calendar for edit modal ---
function OccurrencesEditor({ occurrences = [], onChange }) {
  const updateOccurrence = (index, field, value) => {
    const updated = occurrences.map((occ, i) =>
      i === index ? { ...occ, [field]: value } : occ
    );
    onChange(updated);
  };

  const addOccurrenceToDay = (dateKey) => {
    onChange([...occurrences, makeEmptyOccurrence(dateKey)]);
  };

  // Map each occurrence's stable key to its global index.
  // Keyed by identity (_key or id) rather than position so the lookup is
  // order-independent — CalendarGrid sorts dayOccurrences by local_time
  // which can differ from the original array order.
  const occurrenceIndexMap = useMemo(() => {
    const map = {};
    occurrences.forEach((occ, idx) => {
      const key = occ._key || occ.id;
      if (key != null) map[key] = idx;
    });
    return map;
  }, [occurrences]);

  return (
    <CalendarGrid
      occurrences={occurrences}
      allowSelectEmpty
      renderDayDetail={({ selectedDay, dayOccurrences }) =>
        selectedDay ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">{format(selectedDay, "EEEE, MMMM d")}</h4>
              <span className="text-xs text-muted-foreground">
                {dayOccurrences.length} {dayOccurrences.length === 1 ? "show" : "shows"}
              </span>
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {dayOccurrences.map((occ) => {
                const globalIdx = occurrenceIndexMap[occ._key || occ.id];
                if (globalIdx === undefined) return null;
                return (
                  <div key={occ._key ?? globalIdx} className="space-y-2 p-2 rounded-md bg-muted/50 text-sm">
                    <input
                      type="text"
                      value={occ.name || ""}
                      onChange={(e) => updateOccurrence(globalIdx, "name", e.target.value)}
                      placeholder="Occurrence name"
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={occ.local_time?.substring(0, 5) || ""}
                        onChange={(e) => updateOccurrence(globalIdx, "local_time", e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <select
                        value={occ.status || "onsale"}
                        onChange={(e) =>
                          onChange(occurrences.map((o, i) => (i === globalIdx ? withStatus(o, e.target.value) : o)))
                        }
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="onsale">On Sale</option>
                        <option value="sold_out">Sold Out</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="postponed">Postponed</option>
                        <option value="rescheduled">Rescheduled</option>
                        <option value="presale">Presale</option>
                      </select>
                      <KeepCancelledToggle
                        occ={occ}
                        onChange={(keep) => updateOccurrence(globalIdx, "keep_cancelled", keep)}
                      />
                      <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={occ.is_valid !== false}
                          onChange={(e) => updateOccurrence(globalIdx, "is_valid", e.target.checked)}
                          className="accent-primary"
                        />
                        Valid
                      </label>
                    </div>
                    <div className="space-y-1">
                      {(occ.urls || []).map((urlRef, urlIndex) => (
                        <UrlRefRow
                          // Index-keyed, not URL-keyed: keying on the edited value remounts the
                          // row on every keystroke, which drops focus mid-edit.
                          key={`url-${urlIndex}`}
                          urlRef={urlRef}
                          size="sm"
                          {...urlRefRowHandlers(occ.urls || [], urlIndex, (urls) =>
                            updateOccurrence(globalIdx, "urls", urls)
                          )}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => updateOccurrence(globalIdx, "urls", [...(occ.urls || []), { provider: "", url: "" }])}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Add occurrence URL
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => addOccurrenceToDay(format(selectedDay, "yyyy-MM-dd"))}
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add show to this day
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
            Click a day to edit its shows
          </div>
        )
      }
    />
  );
}

/**
 * One artist on the event, with its billing position editable in place.
 *
 * Rank is the one part of a line-up that is routinely wrong and routinely knowable: ingestion
 * copies whatever order a provider's payload happened to use, and a support act listed second on
 * a poster is not necessarily billed second. Everything else about the artist belongs to the
 * artist, and is edited on its own page.
 *
 * Empty clears the rank instead of keeping the old one — "we do not know the billing order" is a
 * real answer, and the honest one for most ingested line-ups.
 */
export function EventArtistRow({ artist, eventId, onSaved, onError, canHide = true }) {
  const [rank, setRank] = useState(artist.performance_rank ?? "");
  const [saving, setSaving] = useState(false);
  const stored = artist.performance_rank ?? null;
  const parsed = parseRankInput(rank);
  // Nothing to save until the box says something different from the event: every save is an event
  // update and a reindex, and a Save that sits there on an untouched row invites both for nothing.
  const changed = !rankIsUnusable(rank) && parsed !== stored;

  const save = async () => {
    setSaving(true);
    try {
      await eventsApi.setArtistRank(eventId, artist.id, parsed);
    } catch (err) {
      console.error("Error setting the artist's billing position:", err);
      onError(err.response?.data?.detail || "Could not change the billing position");
      setSaving(false);
      return;
    }
    // The write landed. Everything past here is a read-back, and its failure is a different fact:
    // reported as a failed save it sends the admin to redo a change the event already carries.
    try {
      await onSaved(
        parsed === null
          ? `Cleared the billing position for ${capitalize(artist.name)}`
          : `${capitalize(artist.name)} is now ${rankLabel(parsed)}`
      );
    } catch (err) {
      console.error("Saved, but re-reading the event failed:", err);
      onError("Saved. The event could not be re-read — reload to see its current state.");
    } finally {
      setSaving(false);
    }
  };

  // The curated label, as every confirm dialog shows an artist.
  const label = capitalize(displayLabel(artist.preferred_name, artist.name));

  const hide = async () => {
    if (!window.confirm(`Hide ${label} on this event? It can be restored, and a re-fetch will not bring it back.`)) return;
    setSaving(true);
    try {
      await eventsApi.hideArtist(eventId, artist.id);
    } catch (err) {
      console.error("Error hiding the artist on the event:", err);
      onError(apiErrorMessage(err, "Could not hide the artist"));
      setSaving(false);
      return;
    }
    // The row unmounts with the re-read; a failed re-read is reported like a rank save's.
    try {
      await onSaved(`Hid ${label} on the event`);
    } catch (err) {
      console.error("Hidden, but re-reading the event failed:", err);
      onError("Hidden. The event could not be re-read — reload to see its current state.");
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Music className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <EntityLink type="artist" id={artist.id} className="text-sm font-medium">{capitalize(artist.name)}</EntityLink>
      {artist.genres?.map((g) => (
        <EntityLink key={g.id || g.name} type="genre" id={g.id}>
          <Badge variant="outline" className="text-[10px] py-0">{capitalize(g.name)}</Badge>
        </EntityLink>
      ))}
      {/* Sent only when they differ from `genres`, which are the curated ones when the artist has some (#718). */}
      {artist.provider_genres?.length > 0 && (
        <span className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          Provider:
          {artist.provider_genres.map((g) => (
            <EntityLink key={g.id || g.name} type="genre" id={g.id}>
              <Badge variant="secondary" className="text-[10px] py-0">{capitalize(g.name)}</Badge>
            </EntityLink>
          ))}
        </span>
      )}
      {artist.country && (
        <span className="text-xs text-muted-foreground">{capitalize(artist.country)}</span>
      )}
      {/* A ref with no artist id is read-only: the endpoint addresses one ref BY id, so its box
          would offer a save that can only answer 404. Merging two events keeps id-less refs on
          the canonical (see `merge_event_artists`), so these do reach this list. */}
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        {artist.id ? (
          <>
            <BillingRankInput value={rank} onChange={setRank} name={capitalize(artist.name)} disabled={saving} />
            {changed && (
              <Button size="sm" variant="outline" className="h-7" disabled={saving} onClick={save}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
              </Button>
            )}
            {canHide && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                disabled={saving}
                onClick={hide}
                aria-label={`Hide ${label} on this event`}
                title="Hide on this event"
              >
                <EyeOff className="h-3.5 w-3.5" />
              </Button>
            )}
          </>
        ) : (
          rankLabel(stored) && <span>{rankLabel(stored)}</span>
        )}
      </span>
    </div>
  );
}

/**
 * An artist an admin hid on the event (`hidden_artists`): off the line-up every reader sees, kept
 * here so the event remembers it. Restore puts it back at the rank it had, unless another act holds
 * it now (the API decides).
 */
export function HiddenEventArtistRow({ hidden, eventId, onSaved, onError, canRestore = true }) {
  const [saving, setSaving] = useState(false);
  const name = capitalize(displayLabel(hidden.preferred_name, hidden.name) || hidden.id);

  const restore = async () => {
    setSaving(true);
    let result;
    try {
      // No rank sent: the API restores the one the entry kept, unless another act holds it now.
      result = await eventsApi.addArtist(eventId, hidden.id);
    } catch (err) {
      console.error("Error restoring the artist on the event:", err);
      onError(apiErrorMessage(err, "Could not restore the artist"));
      setSaving(false);
      return;
    }
    try {
      // A deleted or merged artist cannot come back; the API only drops its record and says so.
      await onSaved(result?.record_only ? result.message : `Restored ${name} on the event`);
    } catch (err) {
      console.error("Restored, but re-reading the event failed:", err);
      onError("Restored. The event could not be re-read — reload to see its current state.");
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
      <EyeOff className="h-3.5 w-3.5 shrink-0" />
      <div>
        <EntityLink type="artist" id={hidden.id} className="text-sm line-through">{name}</EntityLink>
        <ProviderLabel preferred={hidden.preferred_name} original={hidden.name} />
      </div>
      <span className="text-xs" title={hidden.hidden_by ? `Admin ${hidden.hidden_by}` : undefined}>
        Hidden{hidden.hidden_at ? ` ${formatDate(hidden.hidden_at)}` : ""}
      </span>
      {canRestore && (
        <Button size="sm" variant="outline" className="h-7" disabled={saving} onClick={restore}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Restore"}
        </Button>
      )}
    </div>
  );
}

/**
 * Put an existing catalogue artist on the event's line-up, optionally at a billing position.
 * Only existing artists: creating one belongs on the artists page, where its identity is set.
 */
export function AddEventArtist({ eventId, existingIds, onSaved, onError }) {
  const [picked, setPicked] = useState(null);
  const [rank, setRank] = useState("");
  const [saving, setSaving] = useState(false);
  const already = picked && existingIds.includes(picked.id);

  const add = async () => {
    setSaving(true);
    try {
      await eventsApi.addArtist(eventId, picked.id, parseRankInput(rank));
    } catch (err) {
      console.error("Error adding the artist to the event:", err);
      onError(apiErrorMessage(err, "Could not add the artist"));
      setSaving(false);
      return;
    }
    const name = picked.label || picked.id;
    setPicked(null);
    setRank("");
    try {
      await onSaved(`Added ${capitalize(name)} to the event`);
    } catch (err) {
      console.error("Added, but re-reading the event failed:", err);
      onError("Added. The event could not be re-read — reload to see its current state.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-[16rem] flex-1">
        <EntitySearchPicker
          placeholder="Add an artist: search by name or paste an ID..."
          search={(query, config) =>
            artistsApi.getAll({ search: query, page_size: 8 }, config).then((data) => data.artists || [])
          }
          optionLabel={(a) => displayLabel(a.preferred_name, a.name)}
          value={picked}
          onChange={setPicked}
          disabled={saving}
          allowRawId={false}
        />
      </div>
      {picked && (
        <>
          <BillingRankInput value={rank} onChange={setRank} name={picked.label || picked.id} disabled={saving} />
          <Button size="sm" className="h-8" disabled={saving || already || rankIsUnusable(rank)} onClick={add}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="mr-1 h-3.5 w-3.5" />Add</>}
          </Button>
          {already && <span className="text-xs text-muted-foreground">Already on this event</span>}
        </>
      )}
    </div>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const { searchInput, setSearchInput, search: searchFilter, submitSearch, clearSearch } = useSearchFilter();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // The artist this event's classification was copied from, or null when it owns its profile.
  const inheritedArtist = inheritedClassificationArtist(selectedEvent);
  const isReclassify = hasClassificationProfile(selectedEvent?.classificationProfile);

  // Classify / Re-classify on the detail modal: the call is one synchronous LLM request.
  const classify = useClassify(eventsApi.classify);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [editError, setEditError] = useState(null);
  const { suspiciousUrls: editSuspiciousImages, check: checkSuspiciousImages } = useSuspiciousImages();
  const [isSaving, setIsSaving] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Manual duplicate: pick an event to flag as a duplicate of `selectedEvent` (the canonical).
  const [isMarkDuplicateOpen, setIsMarkDuplicateOpen] = useState(false);
  const [dupPicked, setDupPicked] = useState(null);
  const [dupOnlyMark, setDupOnlyMark] = useState(false);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupError, setDupError] = useState(null);

  const [activeTab, setActiveTab] = useState("events");

  const [venueSearch, setVenueSearch] = useState("");
  const [venueResults, setVenueResults] = useState([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueDropdownOpen, setVenueDropdownOpen] = useState(false);
  const debouncedVenueSearch = useDebouncedValue(venueSearch, 300);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [allEventsCache, setAllEventsCache] = useState(null);
  const allEventsRequestRef = useRef(0);
  const [venueFilter, setVenueFilter] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [domainFilter, setDomainFilter] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
  // "" = all, "yes" = has classification_profile, "no" = missing
  const [classifiedFilter, setClassifiedFilter] = useState("");
  // "" = all, else one INGESTION_SOURCES key matched against the event's source_ids
  const [sourceFilter, setSourceFilter] = useState("");
  const [duplicateReviewCount, setDuplicateReviewCount] = useState(0);
  const duplicateReviewCountRequestRef = useRef(0);
  const [imageReviewCount, setImageReviewCount] = useState(0);
  const editErrorRef = useRef(null);
  const isDefaultStatusFilter = arraysEqual(statusFilter, DEFAULT_STATUS_FILTER);

  // When a save fails, the error banner sits at the top of a long scrollable modal
  // while the Save button is at the bottom — scroll it into view so the user sees it.
  useEffect(() => {
    if (editError && editErrorRef.current) {
      editErrorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [editError]);

  // Whether we need the full dataset (client-side filter or non-server sort active)
  const needsFullDataset = (sortKey && !SERVER_SORT_KEYS.has(sortKey)) || venueFilter.length > 0 || domainFilter.length > 0;

  // Every filter the server applies, built once. Both loaders spread it and the full-dataset
  // cache invalidation depends on it, so the filter states are listed in exactly one place —
  // a filter added here cannot be forgotten by one of the three and silently go stale.
  const serverFilterParams = useMemo(() => {
    const params = {};
    if (searchFilter) params.search = searchFilter;
    if (cityFilter) params.city = cityFilter;
    if (arraysEqual(statusFilter, DEFAULT_STATUS_FILTER)) {
      params.exclude_status = "duplicate";
    } else if (statusFilter.length === 1) {
      params.status = statusFilter[0];
    } else if (statusFilter.length < EVENT_STATUS_OPTIONS.length) {
      params.statuses = statusFilter.join(",");
    }
    Object.assign(params, classifiedFilterParams(classifiedFilter));
    if (sourceFilter) params.source = sourceFilter;
    return params;
  }, [searchFilter, cityFilter, statusFilter, classifiedFilter, sourceFilter]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...serverFilterParams, page, page_size: pageSize };
      if (SERVER_SORT_KEYS.has(sortKey)) {
        params.sort_by = sortKey;
        params.sort_order = sortDir;
      }

      const data = await eventsApi.getAll(params);
      const mapped = (data.events || []).map(mapApiEventToDisplayFormat);
      setEvents(mapped);
      setPagination(data.pagination || null);
      // Collect domains incrementally from each page loaded
      setAllDomains((prev) => {
        const set = new Set(prev);
        mapped.forEach((ev) => (ev.domain || []).forEach((d) => set.add(d)));
        return set.size !== prev.length ? [...set].sort((a, b) => a.localeCompare(b)) : prev;
      });
    } catch (err) {
      console.error("Error fetching events:", err);
      if (!err.response) {
        setError("Could not connect to the server. Please try again.");
      } else {
        setError(err.response?.data?.detail || "Error loading events");
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, serverFilterParams, sortKey, sortDir]);

  useEffect(() => {
    // Only use server pagination when full dataset is NOT needed
    if (!needsFullDataset) fetchEvents();
  }, [fetchEvents, needsFullDataset]);
  // The paged reload as of the latest render, for work that finishes long after it started (a
  // classify call runs up to minutes): its own closure holds that moment's page and filters.
  const refreshPagedListRef = useRef(null);
  refreshPagedListRef.current = () => {
    if (!needsFullDataset) fetchEvents();
  };

  const applyDuplicateReviewCount = useCallback((count) => {
    duplicateReviewCountRequestRef.current += 1;
    setDuplicateReviewCount(count);
  }, []);

  const loadDuplicateReviewCount = useCallback(async () => {
    const requestId = ++duplicateReviewCountRequestRef.current;
    try {
      const data = await eventsApi.getPossibleDuplicates(1);
      if (requestId === duplicateReviewCountRequestRef.current) {
        setDuplicateReviewCount(data.total_groups || 0);
      }
    } catch {
      if (requestId === duplicateReviewCountRequestRef.current) {
        setDuplicateReviewCount(0);
      }
    }
  }, []);

  const loadImageReviewCount = useCallback(async () => {
    try {
      const data = await imageReviewApi.getCount();
      setImageReviewCount(data.total || 0);
    } catch {
      setImageReviewCount(0);
    }
  }, []);

  useEffect(() => {
    loadDuplicateReviewCount();
    loadImageReviewCount();
  }, [loadDuplicateReviewCount, loadImageReviewCount]);

  // Load all venues and configured city filters on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await venuesApi.getAll({ page: 1, page_size: 100 });
        if (cancelled) return;
        let venues = (data.venues || []).map((v) => ({ id: v.id, name: capitalize(v.name), city: v.city || "" }));
        const total = data.pagination?.total_pages || 1;
        if (total > 1) {
          const extra = await Promise.all(
            Array.from({ length: Math.min(total - 1, 9) }, (_, i) =>
              venuesApi.getAll({ page: i + 2, page_size: 100 })
            )
          );
          if (cancelled) return;
          extra.forEach((d) => (d.venues || []).forEach((v) => venues.push({ id: v.id, name: capitalize(v.name), city: v.city || "" })));
        }
        venues.sort((a, b) => a.name.localeCompare(b.name));
        setAllVenues(venues);
      } catch { /* ignore — venues are best-effort */ }

      // Domains are collected incrementally from each fetchEvents call — no upfront scan needed
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch venue autocomplete results when the debounced search term settles.
  // Cancellation guards against a slow earlier request overwriting a newer one.
  useEffect(() => {
    const query = debouncedVenueSearch.trim();
    if (!query) {
      setVenueResults([]);
      return undefined;
    }
    let cancelled = false;
    setVenueLoading(true);
    venuesApi
      .getAll({ search: query, page_size: 10 })
      .then((data) => {
        if (!cancelled) setVenueResults(data.venues || []);
      })
      .catch(() => {
        if (!cancelled) setVenueResults([]);
      })
      .finally(() => {
        if (!cancelled) setVenueLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedVenueSearch]);

  // Auto-dismiss success and error messages after 4s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const handleSearch = (e) => {
    submitSearch(e);
    setPage(1);
  };

  const handleClearSearch = () => {
    clearSearch();
    setPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleVenueSearch = (query) => {
    // The actual lookup is debounced in an effect keyed on `debouncedVenueSearch`.
    setVenueSearch(query);
    setVenueDropdownOpen(query.trim() !== "");
  };

  const handleSelectVenue = (venue) => {
    setEditData((d) => ({ ...d, venueId: venue.id, venueName: venue.name }));
    setVenueSearch("");
    setVenueResults([]);
    setVenueDropdownOpen(false);
  };

  const handleOpenDetail = (event) => {
    setSelectedEvent(event);
    setIsDetailModalOpen(true);
  };

  const handleClassify = async () => {
    // A click always classifies the event itself, so an inherited event gets its own profile.
    const confirmText = inheritedArtist
      ? `Re-classify this event on its own? It now inherits ${inheritedArtist.name || "its artist"}'s classification; the event's own result replaces it (one LLM call) and stops following the artist. To change every event of the artist, re-classify the artist instead.`
      : "Re-classify this event? The classifier's new result replaces the current classification (one LLM call). If it fails, the current one is kept.";
    if (isReclassify && !window.confirm(confirmText)) return;
    const eventId = selectedEvent.id;
    await classify.run(eventId);
    // Always re-read: besides a new profile or marker, another run may have classified the event
    // meanwhile, and a client timeout can hide a save the server made. Its own catch: a failed
    // re-read must not report a paid-for classification as failed.
    refreshSelectedEvent(eventId).catch((err) => console.error("Error reloading event:", err));
  };

  const loadEventById = async (eventId) => {
    const data = await eventsApi.getById(eventId);
    return mapApiEventToDisplayFormat(data?.event ?? data);
  };

  // Re-reads one event into the modal after a write — unless the admin has since opened another.
  //
  // After a billing change it re-reads rather than patching `artistsData`: the rank that matters
  // is the one the event now carries, and the row is keyed on it so it resyncs.
  //
  // The list is refreshed with it, as every other write on this page does. The detail modal is
  // opened FROM a row, so a stale row reopens the card seeded with the rank that was just
  // replaced — which reads as a dropped save and invites writing the old value back. The sort
  // cache holds the same stale copies, hence clearing it.
  //
  // Both happen BEFORE the detail re-read, which is the half that can throw: the write has
  // already landed, so the list is stale from that moment on, and leaving the refresh downstream
  // of a failed read is how it stays stale exactly when the admin is told to reload.
  const refreshSelectedEvent = async (eventId = selectedEvent.id) => {
    setAllEventsCache(null); // reloads the full dataset when the page runs off it
    refreshPagedListRef.current();
    const loaded = await loadEventById(eventId);
    setSelectedEvent((current) => (current?.id === eventId ? loaded : current));
  };

  const handleLineupSaved = async (message) => {
    setSuccess(message);
    await refreshSelectedEvent();
  };

  // Deep link: `?event=<id>` (used by the consumer web app's admin shortcut) opens
  // that event's detail modal on load. We fetch the single event directly rather
  // than searching the list.
  useEntityDeepLink("event", async (eventId) => {
    setActiveTab("events");
    try {
      handleOpenDetail(await loadEventById(eventId));
    } catch (err) {
      console.error("Error opening event from deep link:", err);
      setError(err.response?.data?.detail || "Could not open the requested event");
    }
  });

  const handleOpenEdit = (event) => {
    setEditError(null);
    setSelectedEvent(event);
    setEditData({
      preferredTitle: event.preferredTitle || "",
      visibility: event.status === "hidden" ? "hidden" : "visible",
      activatePending: false,
      description: event.description || "",
      aiDescription: event.aiDescription || "",
      urls: event.urls?.length > 0 ? event.urls.map((urlRef, i) => ({ _key: urlRef.url || `url-${i}`, ...urlRef })) : [{ _key: `url-${Date.now()}`, provider: "", url: "" }],
      links: event.links && Object.keys(event.links).length > 0
        ? linksObjectToRows(event.links)
        : [{ _key: `link-${Date.now()}`, key: "", value: "" }],
      domain: (event.domain || []).join(", "),
      price: event.startPrice?.price ?? "",
      currency: event.startPrice?.currency || "EUR",
      durationMinutes: event.durationMinutes ?? "",
      minAge: event.minAge ?? "",
      images: normalizeEventImages(event.images),
      primaryImageUrl: event.enrichmentMetadata?.primary_image_locked ? event.primaryImageUrl : null,
      mobileImageUrl: event.enrichmentMetadata?.mobile_image_locked ? event.mobileImageUrl : null,
      venueId: event.venueId || "",
      venueName: event.venue || "",
      mainPopulation: event.mainPopulation || "",
      occurrences: event.occurrences?.length > 0
        ? event.occurrences.map((occ, i) => ({
            _key: occ.id || `occ-${i}`,
            id: occ.id || null,
            name: occ.name || "",
            local_date: occ.local_date || "",
            local_time: occ.local_time?.substring(0, 5) || "",
            status: occ.status || "onsale",
            keep_cancelled: initialKeepCancelled(occ),
            urls: normalizeUrlRefs(occ.urls || []),
            is_valid: occ.is_valid ?? null,
            doors_time: occ.doors_time || "",
            set_times: occ.set_times || [],
          }))
        : [makeEmptyOccurrence()],
    });
    setVenueSearch("");
    setVenueResults([]);
    setVenueDropdownOpen(false);
    // Flag known-placeholder images so the editor can offer a bulk remove.
    checkSuspiciousImages(normalizeEventImages(event.images).map((image) => image.url));
    setIsEditModalOpen(true);
  };

  const handleOpenEventEditById = async (eventId) => {
    handleOpenEdit(await loadEventById(eventId));
  };

  const handleEventImagesChange = (nextUrls) => {
    setEditData((data) => {
      return {
        ...data,
        images: reconcileImageUrls(data.images, nextUrls),
        primaryImageUrl: data.primaryImageUrl && nextUrls.includes(data.primaryImageUrl)
          ? data.primaryImageUrl
          : null,
        mobileImageUrl: data.mobileImageUrl && nextUrls.includes(data.mobileImageUrl)
          ? data.mobileImageUrl
          : null,
      };
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedEvent) return;
    setIsSaving(true);
    setEditError(null);
    try {
      const updatePayload = {};

      if (editData.preferredTitle !== (selectedEvent.preferredTitle || "")) {
        updatePayload.preferred_title = editData.preferredTitle || null;
      }
      if (editData.description !== (selectedEvent.description || "")) {
        updatePayload.description = editData.description || null;
      }
      if (editData.aiDescription !== (selectedEvent.aiDescription || "")) {
        // Blank clears it, and the event falls back to the ingested description — which is still
        // stored, so this is an undo rather than a deletion.
        updatePayload.ai_description = editData.aiDescription || null;
      }

      const newUrls = normalizeUrlRefs(editData.urls || []);
      if (!arraysEqual(newUrls, selectedEvent.urls || [])) {
        updatePayload.urls = newUrls;
      }

      const newLinks = linkRowsToObject(editData.links || []);
      if (JSON.stringify(newLinks) !== JSON.stringify(selectedEvent.links || {})) {
        updatePayload.links = Object.keys(newLinks).length > 0 ? newLinks : null;
      }

      const arrayFields = ["domain"];
      for (const field of arrayFields) {
        const newVal = parseCommaSeparated(editData[field]);
        if (!arraysEqual(newVal, selectedEvent[field] || [])) {
          updatePayload[field] = newVal;
        }
      }

      if (editData.price !== "" && editData.price !== null) {
        const priceNum = parseFloat(editData.price);
        if (!isNaN(priceNum)) {
          const oldPrice = selectedEvent.startPrice;
          if (!oldPrice || oldPrice.price !== priceNum || oldPrice.currency !== editData.currency) {
            updatePayload.start_price = { price: priceNum, currency: editData.currency };
          }
        }
      }

      // Blank clears either. Fetches only fill an empty one, so an edit stands — but a value the
      // provider states comes back after a clear (#792).
      for (const [field, key, label, max] of [
        ["durationMinutes", "duration_minutes", "Run time", 1440],
        ["minAge", "min_age", "Minimum age", 99],
      ]) {
        const { value, error } = parseWholeNumberInput(editData[field], label, max);
        if (error) {
          setEditError(error);
          return;
        }
        if (value !== (selectedEvent[field] ?? null)) {
          updatePayload[key] = value;
        }
      }

      // Images
      const newImages = normalizeEventImages(editData.images);
      if (!arraysEqual(newImages, normalizeEventImages(selectedEvent.images))) {
        updatePayload.images = newImages;
      }
      const desktopWasLocked = Boolean(selectedEvent.enrichmentMetadata?.primary_image_locked);
      const mobileWasLocked = Boolean(selectedEvent.enrichmentMetadata?.mobile_image_locked);
      if (
        editData.primaryImageUrl !== (desktopWasLocked ? selectedEvent.primaryImageUrl : null)
      ) {
        updatePayload.primary_image_url = editData.primaryImageUrl || null;
      }
      if (
        editData.mobileImageUrl !== (mobileWasLocked ? selectedEvent.mobileImageUrl : null)
      ) {
        updatePayload.mobile_image_url = editData.mobileImageUrl || null;
      }

      // Venue
      if (editData.venueId && editData.venueId !== (selectedEvent.venueId || "")) {
        updatePayload.venue_id = editData.venueId;
      }

      // Main population — only settable while venue-less (field is hidden otherwise)
      if (!editData.venueId && editData.mainPopulation !== (selectedEvent.mainPopulation || "")) {
        updatePayload.main_population = editData.mainPopulation || null;
      }

      // Occurrences
      const loadedOccurrences = new Map((selectedEvent.occurrences || []).filter((o) => o.id).map((o) => [o.id, o]));
      const newOccurrences = (editData.occurrences || [])
        .filter((occ) => occ.local_date)
        .map((occ) => ({
          ...(occ.id ? { id: occ.id } : {}),
          name: occ.name?.trim() || null,
          local_date: occ.local_date,
          local_time: occ.local_time ? `${occ.local_time}:00` : null,
          status: occ.status || "onsale",
          ...keepCancelledPayload(occ, occ.keep_cancelled),
          urls: normalizeUrlRefs(occ.urls || []),
          is_valid: occ.is_valid ?? null,
          ...changedOccurrenceTimes(occ, loadedOccurrences.get(occ.id)),
        }));
      const oldOccurrences = (selectedEvent.occurrences || []).map((occ) => ({
        ...(occ.id ? { id: occ.id } : {}),
        name: occ.name || null,
        local_date: occ.local_date,
        local_time: occ.local_time || null,
        status: occ.status || "onsale",
        ...keepCancelledPayload(occ, initialKeepCancelled(occ)),
        urls: normalizeUrlRefs(occ.urls || []),
        is_valid: occ.is_valid ?? null,
      }));
      if (!arraysEqual(newOccurrences, oldOccurrences)) {
        updatePayload.occurrences = newOccurrences;
      }

      // Visibility (hide/unhide) is a separate admin action from the field PUT.
      // Only active⇆hidden is offered (see Visibility section); the toggle is
      // disabled for pending/duplicate, so `visibilityChanged` can only be true
      // for the active/hidden states.
      const currentlyHidden = selectedEvent.status === "hidden";
      const wantsHidden = editData.visibility === "hidden";
      const visibilityToggleable =
        selectedEvent.status === "active" || selectedEvent.status === "hidden";
      const visibilityChanged = visibilityToggleable && wantsHidden !== currentlyHidden;

      // Promote pending → active (admin override). Only offered for pending events
      // (see Visibility section's pending branch); the backend blocks the promote
      // when the event is still in an open duplicate-review pair.
      const activateRequested =
        selectedEvent.status === "pending" && editData.activatePending === true;

      if (Object.keys(updatePayload).length === 0 && !visibilityChanged && !activateRequested) {
        setIsEditModalOpen(false);
        return;
      }

      const messages = [];

      if (Object.keys(updatePayload).length > 0) {
        const result = await eventsApi.update(selectedEvent.id, updatePayload);
        messages.push(`fields: ${result.updated_fields?.join(", ") || "OK"}`);
        // A time edit also reaches the show's other tiers that held the old value (#792).
        const followed = result.tiers_followed || [];
        if (followed.length > 0) {
          const names = followed.map((tier) => tier.name || tier.occurrence_id).join(", ");
          messages.push(`same time change applied to ${followed.length} other ticket tier(s): ${names}`);
        }
      }

      // hide/unhide/activate return 200 only on success and 4xx otherwise, so a
      // failed call rejects and is handled by the catch below (which surfaces the
      // backend's specific message via err.response.data.detail) — no success flag
      // to branch on here.
      if (visibilityChanged) {
        await (wantsHidden ? eventsApi.hide(selectedEvent.id) : eventsApi.unhide(selectedEvent.id));
        messages.push(wantsHidden ? "hidden" : "unhidden");
      }

      if (activateRequested) {
        await eventsApi.activate(selectedEvent.id);
        messages.push("activated");
      }

      setSuccess(`Event updated: ${messages.join("; ")}`);
      setIsEditModalOpen(false);
      setSelectedEvent(null);
      setAllEventsCache(null);
      fetchEvents();
    } catch (err) {
      console.error("Error updating event:", err);
      setEditError(apiErrorMessage(err, "Error updating event"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = (event) => {
    setSelectedEvent(event);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    setIsDeleting(true);
    setError(null);
    try {
      await eventsApi.delete(selectedEvent.id);
      setSuccess("Event deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedEvent(null);
      setAllEventsCache(null);
      fetchEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      setError(err.response?.data?.detail || "Error deleting event");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenMarkDuplicate = () => {
    setDupPicked(null);
    setDupOnlyMark(false);
    setDupError(null);
    setIsMarkDuplicateOpen(true);
  };

  const handleMarkDuplicate = async () => {
    if (!selectedEvent || !dupPicked) return;
    setDupBusy(true);
    setDupError(null);
    try {
      // The picked event becomes the duplicate; the open event is the canonical.
      const result = await eventsApi.mergeInto(dupPicked.id, selectedEvent.id, !dupOnlyMark);
      // Events already merged into the picked one now undo only after this merge does (#667).
      const chained = result?.merged_into_source
        ? `. It held ${result.merged_into_source} merged event(s): their undo now waits for this one's`
        : "";
      setSuccess(
        `"${dupPicked.label || dupPicked.id}" marked as duplicate of "${displayLabel(selectedEvent.preferredTitle, selectedEvent.title)}"${chained}`,
      );
      setIsMarkDuplicateOpen(false);
      setIsDetailModalOpen(false);
      setSelectedEvent(null);
      setAllEventsCache(null);
      fetchEvents();
      loadDuplicateReviewCount();
    } catch (err) {
      setDupError(err.response?.data?.detail || "Could not mark as duplicate");
    } finally {
      setDupBusy(false);
    }
  };

  // Load all events for client-side full-dataset sort
  const loadAllEvents = useCallback(async () => {
    // A filter change starts one of these while the previous may still be paging through
    // `total_pages` requests, because the auto-load effect re-runs on the new callback while the
    // cache is still null. Without this guard the superseded run — usually the less filtered one,
    // since it has the most pages to fetch — resolves last and wins, leaving the grid showing
    // rows the current filters exclude and no dependency left to change and trigger a reload.
    const requestId = ++allEventsRequestRef.current;
    const isCurrent = () => requestId === allEventsRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const baseParams = { ...serverFilterParams, page_size: 100 };

      // Fetch page 1 to get correct total_pages for page_size=100
      const first = await eventsApi.getAll({ ...baseParams, page: 1 });
      let all = (first.events || []).map(mapApiEventToDisplayFormat);
      const tp = first.pagination?.total_pages || 1;

      // Fetch remaining pages in batches of 5 to avoid overwhelming the backend
      for (let i = 2; i <= tp; i += 5) {
        // Superseded mid-flight: stop paging rather than spend the rest of the requests on a
        // result that will be discarded. The newer run owns `loading` from here.
        if (!isCurrent()) return;
        const batch = Array.from(
          { length: Math.min(5, tp - i + 1) },
          (_, j) => eventsApi.getAll({ ...baseParams, page: i + j })
        );
        const results = await Promise.allSettled(batch);
        results.forEach((r) => {
          if (r.status === "fulfilled") {
            all = all.concat((r.value.events || []).map(mapApiEventToDisplayFormat));
          }
        });
      }
      if (isCurrent()) setAllEventsCache(all);
    } catch (err) {
      console.error("Error loading all events:", err);
      if (isCurrent()) setError("Error loading events for sorting");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [serverFilterParams]);

  const handleSort = (key) => {
    const newDir = sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key);
    setSortDir(newDir);
    setPage(1);
    if (SERVER_SORT_KEYS.has(key) || !key) {
      setAllEventsCache(null);
    }
  };

  // Auto-load full dataset when needed (client-side venue/domain filter)
  useEffect(() => {
    if (needsFullDataset && !allEventsCache) {
      loadAllEvents();
    }
    if (!needsFullDataset) {
      setAllEventsCache(null);
    }
  }, [needsFullDataset, allEventsCache, loadAllEvents]);

  // Reload the full cache when the server-side filters change. The cache is only refilled while
  // it is null, so a filter that failed to invalidate it would leave a venue- or domain-filtered
  // view showing rows the server would no longer return.
  useEffect(() => {
    if (needsFullDataset) {
      setAllEventsCache(null);
    }
  }, [needsFullDataset, serverFilterParams]);

  // Sync domains from allEventsCache when available
  useEffect(() => {
    if (!allEventsCache) return;
    const domainSet = new Set();
    allEventsCache.forEach((ev) => (ev.domain || []).forEach((d) => domainSet.add(d)));
    const sorted = [...domainSet].sort((a, b) => a.localeCompare(b));
    if (sorted.length > 0) setAllDomains(sorted);
  }, [allEventsCache]);

  const handleCityChange = (value) => {
    setCityFilter(value);
    setPage(1);
  };

  const venueFilterOptions = useMemo(
    () => allVenues.map((v) => ({ value: v.name, label: capitalize(v.name) })),
    [allVenues]
  );

  const domainFilterOptions = useMemo(
    () => allDomains.map((d) => ({ value: d, label: capitalize(d) })),
    [allDomains]
  );

  // One descriptor per column filter, rendered as a TableColumnHeader dropdown
  // on desktop and by MobileFilterBar below `lg`.
  const venueColumnFilter = {
    multiple: true,
    value: venueFilter,
    options: venueFilterOptions,
    onChange: (next) => {
      setVenueFilter(next);
      setPage(1);
    },
  };

  const domainColumnFilter = {
    multiple: true,
    value: domainFilter,
    options: domainFilterOptions,
    onChange: (next) => {
      setDomainFilter(next);
      setPage(1);
    },
  };

  const statusColumnFilter = {
    multiple: true,
    value: statusFilter,
    options: EVENT_STATUS_FILTER_OPTIONS,
    // The default selection is non-empty but still means "unfiltered".
    active: !isDefaultStatusFilter,
    clearLabel: "Reset",
    align: "right",
    // Deselecting everything falls back to that default: the list is never
    // usefully empty, and the filter reads as unfiltered again.
    onChange: (next) => {
      setStatusFilter(next.length > 0 ? normalizeStatusFilter(next) : DEFAULT_STATUS_FILTER);
      setAllEventsCache(null);
      setPage(1);
    },
  };

  const classifiedColumnFilter = {
    value: classifiedFilter,
    options: CLASSIFIED_FILTER_OPTIONS,
    align: "right",
    onChange: (value) => {
      setClassifiedFilter(value);
      setPage(1);
    },
  };

  const sourceColumnFilter = {
    value: sourceFilter,
    options: SOURCE_FILTER_OPTIONS,
    onChange: (value) => {
      setSourceFilter(value);
      setPage(1);
    },
  };

  // The empty state says "try different filters" only when one is actually on. Computed once for
  // both copies of it — the desktop table's and the mobile list's.
  const hasActiveFilters = Boolean(
    searchFilter || cityFilter || !isDefaultStatusFilter || venueFilter.length > 0 || domainFilter.length > 0 || classifiedFilter || sourceFilter
  );

  const mobileColumnFilters = [
    { key: "status", label: "Status", ...statusColumnFilter },
    { key: "domain", label: "Domain", ...domainColumnFilter },
    { key: "source", label: "Source", ...sourceColumnFilter },
    { key: "classified", label: "Classified", ...classifiedColumnFilter },
    { key: "venue", label: "Venue", ...venueColumnFilter },
  ];

  // Full dataset mode: when allEventsCache is loaded, sort + filter + paginate client-side
  // Normal mode: use server-paginated events directly
  const useFullDataset = needsFullDataset && allEventsCache;

  const filteredSortedData = useMemo(() => {
    if (!useFullDataset) return null;
    let data = [...allEventsCache];
    if (sortKey && sortKey !== "date") {
      // Status sorts on what the column shows (`display_status`: a fully cancelled event reads Cancelled).
      const sortValue = (e) => (sortKey === "status" ? e.display_status || e.status : e[sortKey]) || "";
      data.sort((a, b) => sortValue(a).toLowerCase().localeCompare(sortValue(b).toLowerCase()));
      if (sortDir === "desc") data.reverse();
    }
    if (venueFilter.length > 0) {
      data = data.filter((e) => venueFilter.includes(e.venue));
    }
    if (domainFilter.length > 0) {
      data = data.filter((e) => e.domain?.some((d) => domainFilter.includes(d)));
    }
    return data;
  }, [useFullDataset, allEventsCache, sortKey, sortDir, venueFilter, domainFilter]);

  const displayEvents = useMemo(() => {
    if (!filteredSortedData) return events;
    const start = (page - 1) * pageSize;
    return filteredSortedData.slice(start, start + pageSize);
  }, [filteredSortedData, events, page, pageSize]);

  const displayPagination = useMemo(() => {
    if (!filteredSortedData) return pagination;
    const total = filteredSortedData.length;
    return {
      page,
      page_size: pageSize,
      total,
      total_pages: Math.ceil(total / pageSize) || 1,
    };
  }, [filteredSortedData, pagination, page, pageSize]);

  const totalPages = displayPagination?.total_pages || 1;
  const totalEvents = displayPagination?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <h2 className="text-2xl font-bold tracking-tight">Events</h2>
          {activeTab === "events" && (
            <>
              <span className="text-sm text-muted-foreground">({totalEvents})</span>
              <Button variant="ghost" size="icon" onClick={fetchEvents} disabled={loading} title="Refresh">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </>
          )}
          {/* Tab switcher */}
          <div className="flex w-full items-center gap-0.5 overflow-x-auto rounded-md border border-border bg-muted/40 p-0.5 sm:w-auto">
            <button
              onClick={() => setActiveTab("events")}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                activeTab === "events"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              All events
            </button>
            <button
              onClick={() => setActiveTab("duplicates")}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                activeTab === "duplicates"
                  ? "bg-background text-foreground shadow-sm"
                  : duplicateReviewCount > 0
                  ? "text-yellow-700 hover:text-yellow-800"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Duplicates ({duplicateReviewCount})
            </button>
            <button
              onClick={() => setActiveTab("image-review")}
              className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                activeTab === "image-review"
                  ? "bg-background text-foreground shadow-sm"
                  : imageReviewCount > 0
                  ? "text-yellow-700 hover:text-yellow-800"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Image Alerts ({imageReviewCount})
            </button>
          </div>
        </div>
        {activeTab === "events" && <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end xl:w-auto">
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearch}
            onClear={handleClearSearch}
            placeholder="Search by title, artist or event ID"
            loading={loading}
            inputClassName="sm:w-64"
          />
          <CitySelect value={cityFilter} onChange={handleCityChange} />
        </div>}
      </div>

      {/* Messages shared by every events sub-view. */}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>Dismiss</button>
        </div>
      )}

      {activeTab === "duplicates" && (
        <DuplicatesView onQueueChange={applyDuplicateReviewCount} onEditEvent={handleOpenEventEditById} />
      )}
      {activeTab === "image-review" && <ImageReviewView onQueueChange={setImageReviewCount} />}

      {activeTab === "events" && <>
      {/* Events Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading events...</span>
            </div>
          ) : (
            <>
              {/* Mobile filter/sort bar */}
              <div className="border-b border-border px-3 py-2 space-y-2 lg:hidden">
                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">Sort</span>
                  <div className="flex gap-1.5 overflow-x-auto py-0.5">
                    {[
                      { key: "title", label: "Title" },
                      { key: "artist", label: "Artist" },
                      { key: "venue", label: "Venue" },
                      { key: "date", label: "Date" },
                      { key: "status", label: "Status" },
                      { key: "first_seen", label: "First seen" },
                      { key: "date_promoted", label: "Promoted" },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleSort(key)}
                        className={chip(sortKey === key)}
                      >
                        {label}
                        {sortKey === key ? (
                          sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <MobileFilterBar filters={mobileColumnFilters} />
              </div>

              <div className="divide-y divide-border lg:hidden">
                {displayEvents.length === 0 && (
                  <div className="flex flex-col items-center justify-center px-4 py-12 text-center text-muted-foreground">
                    <Database className="h-12 w-12 mb-4" />
                    <p className="text-lg font-medium">No events found</p>
                    <p className="text-sm">
                      {hasActiveFilters ? "Try different filters" : "No events in the system"}
                    </p>
                  </div>
                )}
                {displayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="space-y-3 p-4"
                    onClick={() => handleOpenDetail(event)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleOpenDetail(event)}
                  >
                    <div className="flex items-start gap-3">
                      <EventDisplayImage
                        src={event.image}
                        alt={event.title}
                        imageResolutionStatus={event.imageResolutionStatus}
                        compactLabel
                        className="h-16 w-20 shrink-0 rounded-lg"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug">{displayLabel(event.preferredTitle, event.title)}</p>
                        <ProviderLabel preferred={event.preferredTitle} original={event.title} />
                        <p className="text-xs text-muted-foreground">
                          {event.sources.map(sourceLabel).join(", ")}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <EventStatusBadge status={event.display_status || event.status} />
                          <ClassificationIssueBadge profile={event.classificationProfile} meta={event.enrichmentMetadata} enricher="event_classification" labels={["Classified", "Unclassified"]} />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Artist</p>
                        <p className="mt-1">{event.artist}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Venue</p>
                        <p className="mt-1">{event.venue}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Date</p>
                        <p className="mt-1">
                          {formatDate(event.date)}
                          {event.time ? ` · ${event.time}h` : ""}
                          {event.occurrences.length > 1 ? ` (+${event.occurrences.length - 1})` : ""}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Promoted</p>
                        <p className="mt-1">{formatDate(event.date_promoted)}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {event.domain?.map((d) => (
                        <Badge key={d} variant="outline" className="text-xs">{capitalize(d)}</Badge>
                      ))}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        First seen {formatDate(event.first_seen)}
                      </span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(event)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {/* A duplicate is its merge's undo record: the API refuses to delete one (#667). */}
                        {event.status !== "duplicate" && (
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(event)} title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openOnApplauss(event.id)}
                          title="View on Applauss"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block">
                {/* `table-fixed` hands every column exactly its share and lets anything longer
                    spill over the neighbour rather than widening the column — which is how the
                    header labels ended up written across each other once Source was added. So
                    the percentages below are shares of `min-w`, not of the viewport, each one
                    sized against that column's measured content (the widest being Actions'
                    three 40px icon buttons and "Classified?" beside its filter icon). Below
                    that width the Table's own wrapper scrolls sideways instead. */}
                <Table className="table-fixed min-w-[1600px]">
              <TableHeader>
                <TableRow>
                  {[
                    { key: "title", label: "Title", width: "w-[15%]" },
                    { key: "artist", label: "Artist", width: "w-[7%]" },
                  ].map((col) => (
                    <TableHead key={col.key} className={col.width}>
                      <button
                        onClick={() => handleSort(col.key)}
                        className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded"
                      >
                        {col.label}
                        {sortKey === col.key ? (
                          sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                        )}
                      </button>
                    </TableHead>
                  ))}
                  <TableColumnHeader
                    label="Venue"
                    className="w-[8%]"
                    sortDir={sortKey === "venue" ? sortDir : null}
                    onSort={() => handleSort("venue")}
                    filter={venueColumnFilter}
                  />
                  <TableHead className="w-[8%]">
                    <button
                      onClick={() => handleSort("date")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded"
                    >
                      Date
                      {sortKey === "date" ? (
                        sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                      )}
                    </button>
                  </TableHead>
                  <TableColumnHeader
                    label="Domain"
                    className="w-[9%]"
                    filter={domainColumnFilter}
                  />
                  <TableColumnHeader
                    label="Source"
                    className="w-[10%]"
                    filter={sourceColumnFilter}
                  />
                  <TableColumnHeader
                    label="Status"
                    className="w-[8%]"
                    sortDir={sortKey === "status" ? sortDir : null}
                    onSort={() => handleSort("status")}
                    filter={statusColumnFilter}
                  />
                  <TableColumnHeader
                    label="Classified?"
                    className="w-[9%]"
                    filter={classifiedColumnFilter}
                  />
                  <TableHead className="w-[8%] whitespace-nowrap">
                    <button
                      onClick={() => handleSort("first_seen")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded"
                    >
                      First Seen
                      {sortKey === "first_seen" ? (
                        sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="w-[8%] whitespace-nowrap">
                    <button
                      onClick={() => handleSort("date_promoted")}
                      className="flex items-center gap-1 hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded"
                    >
                      Promoted
                      {sortKey === "date_promoted" ? (
                        sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="w-[10%] whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayEvents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11}>
                      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <Database className="h-12 w-12 mb-4" />
                        <p className="text-lg font-medium">No events found</p>
                        <p className="text-sm">
                          {hasActiveFilters ? "Try different filters" : "No events in the system"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {displayEvents.map((event) => (
                  <TableRow
                    key={event.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleOpenDetail(event)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <EventDisplayImage
                          src={event.image}
                          alt={event.title}
                          imageResolutionStatus={event.imageResolutionStatus}
                          compactLabel
                          className="h-12 w-16 shrink-0 rounded-lg"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{displayLabel(event.preferredTitle, event.title)}</p>
                          <ProviderLabel preferred={event.preferredTitle} original={event.title} className="truncate" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate block">{event.artist}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate block">{event.venue}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm whitespace-nowrap">{formatDate(event.date)}</div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {event.time ? `${event.time}h` : ""}
                        {event.occurrences.length > 1 && (
                          <span className="ml-1">(+{event.occurrences.length - 1})</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-1">
                        {event.domain?.map((d) => (
                          <Badge key={d} variant="outline" className="text-xs">{capitalize(d)}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {event.sources.map((source) => (
                          <Badge key={source} variant="outline" className="text-xs">{sourceLabel(source)}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <EventStatusBadge status={event.display_status || event.status} />
                    </TableCell>
                    <TableCell>
                      <ClassificationIssueBadge profile={event.classificationProfile} meta={event.enrichmentMetadata} enricher="event_classification" labels={["Yes", "No"]} />
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(event.first_seen)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(event.date_promoted)}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(event)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {/* A duplicate is its merge's undo record: the API refuses to delete one (#667). */}
                        {event.status !== "duplicate" && (
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(event)} title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openOnApplauss(event.id)}
                          title="View on Applauss"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background pl-2 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">per page</span>
          </div>
          {totalPages > 1 && (
            <span className="text-sm text-muted-foreground border-l pl-3">
              Page {page} of {totalPages}
            </span>
          )}
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
      </>}

      {/* Detail Modal */}
      <Modal open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <ModalContent title="Event Details" className="max-w-4xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedEvent && (
            <div className="space-y-5">

              {/* Top: Image left + Info right */}
              <div className="flex flex-col gap-5 sm:flex-row">
                <EventDisplayImage
                  src={selectedEvent.image}
                  alt={selectedEvent.title}
                  imageResolutionStatus={selectedEvent.imageResolutionStatus}
                  className="h-44 w-full rounded-lg sm:w-44 sm:shrink-0"
                />
                <div className="flex-1 min-w-0 space-y-3">
                  <div>
                    <h3 className="text-xl font-bold leading-tight">{selectedEvent.title}</h3>
                    <PreferredLabel label="Preferred title" value={selectedEvent.preferredTitle} original={selectedEvent.title} />
                    <p
                      className="text-xs font-mono text-muted-foreground mt-0.5 cursor-pointer hover:text-foreground transition-colors"
                      title="Click to copy"
                      onClick={() => { navigator.clipboard.writeText(selectedEvent.id); }}
                    >
                      ID: {selectedEvent.id}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <EventStatusBadge status={selectedEvent.display_status || selectedEvent.status} />
                      {selectedEvent.domain?.map((d) => (
                        <Badge key={d} variant="outline">{capitalize(d)}</Badge>
                      ))}
                      {selectedEvent.classificationProfile?.primary_event_type && (
                        <Badge variant="secondary">
                          {capitalize(selectedEvent.classificationProfile.primary_event_type)}
                        </Badge>
                      )}
                      {selectedEvent.classificationProfile?.classification_source && (
                        <Badge variant="outline">
                          {capitalize(selectedEvent.classificationProfile.classification_source)}
                        </Badge>
                      )}
                      {selectedEvent.sources.map((source) => (
                        <Badge key={source} variant="secondary">{sourceLabel(source)}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Price */}
                  {selectedEvent.startPrice && (
                    <p className="text-lg font-bold">
                      {selectedEvent.startPrice.price}
                      {selectedEvent.startPrice.currency === "EUR" ? "\u20AC" : ` ${selectedEvent.startPrice.currency}`}
                    </p>
                  )}
                  {(selectedEvent.durationMinutes != null || selectedEvent.minAge != null) && (
                    <p className="text-sm text-muted-foreground">
                      {[
                        selectedEvent.durationMinutes != null && `Runs ${selectedEvent.durationMinutes} min`,
                        selectedEvent.minAge != null && `Ages ${selectedEvent.minAge}+`,
                      ].filter(Boolean).join(" · ")}
                    </p>
                  )}

                  {(selectedEvent.displayUrls?.length > 0 || Object.keys(selectedEvent.links || {}).length > 0) && (
                    <div className="space-y-1.5 text-sm">
                      {selectedEvent.displayUrls?.map((urlRef, i) => (
                        <div key={`${urlRef.url}-${i}`} className="flex min-w-0 items-center gap-2">
                          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="w-20 shrink-0 truncate text-xs text-muted-foreground">
                            {urlRef.provider || "url"}
                          </span>
                          <a
                            href={urlRef.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 truncate text-blue-600 hover:underline"
                          >
                            {urlRef.url}
                          </a>
                          <SeenMeta item={urlRef} />
                        </div>
                      ))}
                      {Object.entries(selectedEvent.links || {}).map(([key, value]) => (
                        <div key={key} className="flex min-w-0 items-center gap-2">
                          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="w-20 shrink-0 truncate text-xs text-muted-foreground">{key}</span>
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 truncate text-blue-600 hover:underline"
                          >
                            {value}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Artists inline. Keyed on the stored rank too, so a saved billing position
                      remounts the row from the reloaded event instead of the box keeping whatever
                      was typed into it. */}
                  <div className="space-y-1.5">
                    {selectedEvent.artistsData?.map((artist, index) => (
                      <EventArtistRow
                        key={`${artist.id || `no-id-${index}`}:${artist.performance_rank ?? ""}`}
                        artist={artist}
                        eventId={selectedEvent.id}
                        onSaved={handleLineupSaved}
                        onError={setError}
                        canHide={selectedEvent.status !== "duplicate"}
                      />
                    ))}
                    {selectedEvent.hiddenArtists.map((hidden) => (
                      <HiddenEventArtistRow
                        key={hidden.id}
                        hidden={hidden}
                        eventId={selectedEvent.id}
                        onSaved={handleLineupSaved}
                        onError={setError}
                        canRestore={selectedEvent.status !== "duplicate"}
                      />
                    ))}
                    {/* A merged-away event is edited on its canonical; the API refuses it too. */}
                    {selectedEvent.status !== "duplicate" && (
                      <AddEventArtist
                        key={selectedEvent.id}
                        eventId={selectedEvent.id}
                        existingIds={(selectedEvent.artistsData || []).map((a) => a.id).filter(Boolean)}
                        onSaved={handleLineupSaved}
                        onError={setError}
                      />
                    )}
                  </div>
                </div>
              </div>

              <ClassifyAction
                busy={classify.isBusy(selectedEvent.id)}
                result={classify.resultFor(selectedEvent.id)}
                isReclassify={isReclassify}
                onClick={handleClassify}
                renderPrevious={(r) => r.previous_profile && <ClassificationProfileDetail profile={r.previous_profile} />}
              >
                <ClassificationIssueBadge profile={selectedEvent.classificationProfile} meta={selectedEvent.enrichmentMetadata} enricher="event_classification" />
              </ClassifyAction>

              <ClassificationProfileDetail profile={selectedEvent.classificationProfile} curatedGenres={selectedEvent.curatedGenres} />

              <DocumentTranslations key={selectedEvent.id} type="event" id={selectedEvent.id} />

              {/* Venue */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Venue</h4>
                <div className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                  <div>
                    <p className="text-muted-foreground text-xs">Name</p>
                    <EntityLink type="venue" id={selectedEvent.venueId} className="font-medium">
                      {selectedEvent.venue}
                    </EntityLink>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">City</p>
                    <p>{capitalize(selectedEvent.venueCity) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Population</p>
                    <p>{capitalize(selectedEvent.mainPopulation) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Address</p>
                    <p>{capitalize(selectedEvent.venueAddress) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Size</p>
                    <p>{capitalize(selectedEvent.venueSize) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Type</p>
                    <p>{capitalize(selectedEvent.venueType) || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Setting</p>
                    <p>{capitalize(selectedEvent.venueSetting) || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Dates ({selectedEvent.occurrences.length})
                </h4>
                {selectedEvent.occurrences.length > 20 ? (
                  <OccurrencesCalendar occurrences={selectedEvent.occurrences} />
                ) : (
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {selectedEvent.occurrences.map((occ, i) => {
                      const statusKey = occ.status?.toLowerCase();
                      const cancelled = cancellationSource(occ);
                      return (
                        <div key={occ.id || `${occ.local_date}-${occ.local_time}-${i}`} className="space-y-1.5 p-2 rounded-md bg-muted/50 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="shrink-0">{formatDate(occ.local_date)}</span>
                              <span className="shrink-0 text-muted-foreground">{formatOccTime(occ.local_time) || "TBA"}h</span>
                              {occ.name && (
                                <span className="min-w-0 truncate text-muted-foreground">
                                  {occ.name}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {occ.is_valid === false && (
                                <Badge variant="destructive" className="text-[10px] py-0">Invalid</Badge>
                              )}
                              <Badge variant={OCC_STATUS[statusKey]?.badge || "secondary"} className="text-[10px] py-0">
                                {OCC_STATUS[statusKey]?.label || occ.status || "Available"}
                              </Badge>
                              {cancelled && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] py-0"
                                  title={
                                    cancelled.kept
                                      ? "Stays cancelled when the provider lists it on sale again"
                                      : "The provider cancelled it; a re-listing puts it back on sale unless Keep cancelled is ticked in the editor"
                                  }
                                >
                                  {cancelled.label}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {normalizeUrlRefs(occ.urls || []).map((urlRef, urlIndex) => (
                            <a
                              key={`${urlRef.url}-${urlIndex}`}
                              href={urlRef.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                "ml-5 block truncate text-xs hover:underline",
                                urlRef.bad ? "text-muted-foreground line-through" : "text-blue-600"
                              )}
                              title={urlRef.bad ? "Marked bad — hidden from consumers" : undefined}
                            >
                              {urlRef.bad ? "bad · " : ""}{(urlRef.provider || "url")}: {urlRef.url}
                            </a>
                          ))}
                          <OccurrenceTimes occ={occ} artists={selectedEvent.artistsData || []} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Description. The AI one is shown first and labelled as what consumers see, because
                  that is the only thing that makes the pair legible — otherwise two descriptions
                  sit side by side with no way to tell which one is live. */}
              {selectedEvent.aiDescription && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    AI description <span className="normal-case">— shown to users</span>
                  </h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed max-h-32 overflow-y-auto">
                    {selectedEvent.aiDescription}
                  </p>
                </div>
              )}
              {selectedEvent.description && (
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Description {selectedEvent.aiDescription && <span className="normal-case">— from the source</span>}
                  </h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed max-h-32 overflow-y-auto">
                    {selectedEvent.description}
                  </p>
                </div>
              )}

              {/* Raw API Data */}
              {selectedEvent._raw && (
                <details className="border-t pt-4">
                  <summary className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2 cursor-pointer hover:text-foreground transition-colors select-none">
                    Raw API Data
                  </summary>
                  <pre className="text-xs font-mono text-muted-foreground bg-muted/50 rounded-lg p-3 max-h-96 overflow-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(selectedEvent._raw, null, 2)}
                  </pre>
                </details>
              )}

              <ModalFooter className="border-t pt-4">
                <Button variant="outline" size="sm" onClick={() => { setIsDetailModalOpen(false); handleOpenEdit(selectedEvent); }}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                {/* A duplicate can't be a canonical: merging into one would chain
                    duplicate_of and bury the absorbed occurrences in an event no
                    consumer sees. The API rejects it too. */}
                {selectedEvent.status !== "duplicate" && (
                  <Button variant="outline" size="sm" onClick={handleOpenMarkDuplicate}>
                    <GitMerge className="h-4 w-4 mr-2" /> Add duplicate
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openOnApplauss(selectedEvent.id)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" /> View on Applauss
                </Button>
                <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); setSelectedEvent(null); }}>
                  Close
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <ModalContent title="Edit Event" className="max-w-4xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedEvent && (
            <div className="space-y-6">

              {/* Header */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <EventDisplayImage
                  src={selectedEvent.image}
                  alt={selectedEvent.title}
                  imageResolutionStatus={selectedEvent.imageResolutionStatus}
                  compactLabel
                  className="h-14 w-20 shrink-0 rounded-lg"
                />
                <div>
                  <h3 className="font-semibold text-lg leading-tight">{selectedEvent.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">Title is read-only</p>
                </div>
              </div>

              {editError && (
                <div
                  ref={editErrorRef}
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-700"
                >
                  <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Couldn&apos;t save changes</p>
                    <p className="mt-0.5">{editError}</p>
                  </div>
                </div>
              )}

              {/* Visibility (admin action) — edits the top-level event lifecycle
                  status only. Separate from the occurrence "source status"
                  (onsale/cancelled) fields below. */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Visibility
                </h4>
                {selectedEvent.status === "active" || selectedEvent.status === "hidden" ? (
                  <div className="space-y-2">
                    <div className="inline-flex rounded-md border border-input overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setEditData((d) => ({ ...d, visibility: "visible" }))}
                        className={`px-4 h-9 text-sm ${editData.visibility === "visible" ? "bg-primary text-primary-foreground" : "bg-background text-foreground"}`}
                      >
                        Visible
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditData((d) => ({ ...d, visibility: "hidden" }))}
                        className={`px-4 h-9 text-sm border-l border-input ${editData.visibility === "hidden" ? "bg-primary text-primary-foreground" : "bg-background text-foreground"}`}
                      >
                        Hidden
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Hidden events are removed from all user-facing endpoints. This is an admin action, separate from the per-showtime status below.
                    </p>
                  </div>
                ) : selectedEvent.status === "pending" ? (
                  <div className="space-y-2">
                    <EventStatusBadge status={selectedEvent.display_status || selectedEvent.status} />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editData.activatePending === true}
                        onChange={(e) => setEditData((d) => ({ ...d, activatePending: e.target.checked }))}
                        className="h-4 w-4 rounded border-input"
                      />
                      Promote to active on save
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Makes the event visible to users. If the event isn&apos;t classified yet, classification runs first (this may take a few seconds). Blocked if the event is in an unresolved duplicate-review pair.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <EventStatusBadge status={selectedEvent.display_status || selectedEvent.status} />
                    <p className="text-xs text-muted-foreground">
                      Duplicate events are managed by merge and undo-duplicate.
                    </p>
                  </div>
                )}
              </div>

              {/* General Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">General</h4>
                <div>
                  <label className="text-sm font-medium">Preferred title</label>
                  <input
                    type="text"
                    value={editData.preferredTitle}
                    onChange={(e) => setEditData((d) => ({ ...d, preferredTitle: e.target.value }))}
                    placeholder={selectedEvent.title}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown to users instead of the original title. Leave empty to display the original title.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData((d) => ({ ...d, description: e.target.value }))}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">AI description</label>
                  <textarea
                    value={editData.aiDescription}
                    onChange={(e) => setEditData((d) => ({ ...d, aiDescription: e.target.value }))}
                    rows={3}
                    maxLength={2000}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown to users instead of the description above. Normally arrives by accepting an
                    event research proposal — edit it here to fix one, or empty it to fall back to the source description.
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Event URLs</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setEditData((d) => ({
                          ...d,
                          urls: [...(d.urls || []), { _key: `url-${Date.now()}`, provider: "", url: "" }],
                        }))
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add URL
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Mark a URL <span className="font-medium">bad</span> to hide a dead/stale ticket link (e.g. an
                    expired Ticketmaster promo). It stays here for reference but is hidden from the consumer app and
                    survives re-ingestion. URLs can't be deleted here: the next fetch would re-add them.
                  </p>
                  <div className="space-y-2">
                    {(editData.urls || []).map((urlRef, idx) => (
                      <UrlRefRow
                        key={urlRef._key ?? idx}
                        urlRef={urlRef}
                        size="lg"
                        {...urlRefRowHandlers(
                          editData.urls || [],
                          idx,
                          (urls) => setEditData((d) => ({ ...d, urls }))
                        )}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Links</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setEditData((d) => ({
                          ...d,
                          links: [...(d.links || []), { _key: `link-${Date.now()}`, key: "", value: "" }],
                        }))
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add link
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(editData.links || []).map((link, idx) => (
                      <div key={link._key ?? idx} className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_2.25rem]">
                        <input
                          type="text"
                          value={link.key || ""}
                          onChange={(e) => {
                            const updated = [...editData.links];
                            updated[idx] = { ...updated[idx], key: e.target.value };
                            setEditData((d) => ({ ...d, links: updated }));
                          }}
                          placeholder="youtube"
                          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        <input
                          type="url"
                          value={link.value || ""}
                          onChange={(e) => {
                            const updated = [...editData.links];
                            updated[idx] = { ...updated[idx], value: e.target.value };
                            setEditData((d) => ({ ...d, links: updated }));
                          }}
                          placeholder="https://..."
                          className="h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => {
                            const updated = editData.links.filter((_, i) => i !== idx);
                            setEditData((d) => ({
                              ...d,
                              links: updated.length > 0 ? updated : [{ _key: `link-${Date.now()}`, key: "", value: "" }],
                            }));
                          }}
                          title="Remove link"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.price}
                      onChange={(e) => setEditData((d) => ({ ...d, price: e.target.value }))}
                      placeholder="0.00"
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Currency</label>
                    <select
                      value={editData.currency}
                      onChange={(e) => setEditData((d) => ({ ...d, currency: e.target.value }))}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="event-duration-minutes">Run time (minutes)</label>
                    <input
                      id="event-duration-minutes"
                      type="number"
                      step="1"
                      min="1"
                      max="1440"
                      value={editData.durationMinutes}
                      onChange={(e) => setEditData((d) => ({ ...d, durationMinutes: e.target.value }))}
                      placeholder="Unknown"
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="event-min-age">Minimum age</label>
                    <input
                      id="event-min-age"
                      type="number"
                      step="1"
                      min="1"
                      max="99"
                      value={editData.minAge}
                      onChange={(e) => setEditData((d) => ({ ...d, minAge: e.target.value }))}
                      placeholder="None stated"
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              </div>

              {/* Classification Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Classification</h4>
                <div>
                  <label className="text-sm font-medium">Domain</label>
                  <input
                    type="text"
                    value={editData.domain}
                    onChange={(e) => setEditData((d) => ({ ...d, domain: e.target.value }))}
                    placeholder="music, theater, comedy..."
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Comma separated</p>
                </div>
                {inheritedArtist ? (
                  <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium">This classification comes from the artist</p>
                        <p className="text-xs text-muted-foreground">
                          It was copied from{" "}
                          <span className="font-medium">
                            {inheritedArtist.name || "the artist"}
                          </span>{" "}
                          and is kept in sync with it, so it is edited on the artist, where the change
                          reaches every event that reused it. To give only this event a classification
                          of its own, use <span className="font-medium">Re-classify</span> in its
                          details — that detaches it from the artist.
                        </p>
                        <Link
                          to={getAdminEntityUrl("artist", inheritedArtist.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit on {inheritedArtist.name || "the artist"}
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 rounded-md bg-muted/30 p-3">
                    <p className="text-xs text-muted-foreground">
                      Event classification is read-only for now — it comes from the classifier.
                    </p>
                    <ClassificationProfileDetail
                      profile={selectedEvent?.classificationProfile}
                      curatedGenres={selectedEvent?.curatedGenres}
                    />
                  </div>
                )}
              </div>

              {/* Venue Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Venue</h4>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={venueDropdownOpen ? venueSearch : editData.venueName || ""}
                        onChange={(e) => handleVenueSearch(e.target.value)}
                        onFocus={() => {
                          setVenueSearch("");
                          setVenueDropdownOpen(true);
                        }}
                        onBlur={() => setTimeout(() => setVenueDropdownOpen(false), 200)}
                        placeholder="Search venues..."
                        className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    {editData.venueId && (
                      <span className="text-xs text-muted-foreground shrink-0 font-mono">
                        {editData.venueId.substring(0, 8)}...
                      </span>
                    )}
                  </div>
                  {venueDropdownOpen && (venueResults.length > 0 || venueLoading) && (
                    <div className="absolute z-50 mt-1 w-full bg-background border border-input rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {venueLoading ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                        </div>
                      ) : (
                        venueResults.map((venue) => (
                          <button
                            key={venue.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center justify-between"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectVenue(venue)}
                          >
                            <span className="font-medium">{venue.name}</span>
                            {venue.city && (
                              <span className="text-xs text-muted-foreground">{venue.city}</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {!editData.venueId && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Main population
                  </h4>
                  <CitySelect
                    value={editData.mainPopulation || ""}
                    onChange={(value) => setEditData((d) => ({ ...d, mainPopulation: value }))}
                    allLabel="Unassigned"
                  />
                  <p className="text-xs text-muted-foreground">
                    This event has no venue, so it has no other way to be matched to a city. Set the
                    city it belongs to so it stays discoverable. Only settable while venue-less —
                    attaching a venue takes over as the source of this value instead.
                  </p>
                </div>
              )}

              {/* Occurrences Section */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Occurrences
                  <span className="ml-2 text-xs font-normal normal-case tracking-normal">
                    ({(editData.occurrences || []).length})
                  </span>
                </h4>
                {(editData.occurrences || []).length > 20 ? (
                  <OccurrencesEditor
                    occurrences={editData.occurrences}
                    onChange={(updated) => setEditData((d) => ({ ...d, occurrences: updated }))}
                  />
                ) : (
                  <div className="space-y-2">
                    {(editData.occurrences || []).map((occ, idx) => (
                      <div key={occ._key ?? idx} className="space-y-2 rounded-md bg-muted/30 p-2">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                          <input
                            type="text"
                            value={occ.name || ""}
                            onChange={(e) => {
                              const updated = [...editData.occurrences];
                              updated[idx] = { ...updated[idx], name: e.target.value };
                              setEditData((d) => ({ ...d, occurrences: updated }));
                            }}
                            placeholder="Occurrence name"
                            className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <input
                            type="date"
                            value={occ.local_date}
                            onChange={(e) => {
                              const updated = [...editData.occurrences];
                              updated[idx] = { ...updated[idx], local_date: e.target.value };
                              setEditData((d) => ({ ...d, occurrences: updated }));
                            }}
                            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <input
                            type="time"
                            value={occ.local_time}
                            onChange={(e) => {
                              const updated = [...editData.occurrences];
                              updated[idx] = { ...updated[idx], local_time: e.target.value };
                              setEditData((d) => ({ ...d, occurrences: updated }));
                            }}
                            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                          <select
                            value={occ.status}
                            onChange={(e) => {
                              const updated = [...editData.occurrences];
                              updated[idx] = withStatus(updated[idx], e.target.value);
                              setEditData((d) => ({ ...d, occurrences: updated }));
                            }}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <option value="onsale">On Sale</option>
                            <option value="sold_out">Sold Out</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="postponed">Postponed</option>
                            <option value="rescheduled">Rescheduled</option>
                            <option value="presale">Presale</option>
                          </select>
                          <KeepCancelledToggle
                            occ={occ}
                            onChange={(keep) => {
                              const updated = [...editData.occurrences];
                              updated[idx] = { ...updated[idx], keep_cancelled: keep };
                              setEditData((d) => ({ ...d, occurrences: updated }));
                            }}
                          />
                          <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer select-none shrink-0">
                            <input
                              type="checkbox"
                              checked={occ.is_valid !== false}
                              onChange={(e) => {
                                const updated = [...editData.occurrences];
                                updated[idx] = { ...updated[idx], is_valid: e.target.checked };
                                setEditData((d) => ({ ...d, occurrences: updated }));
                              }}
                              className="accent-primary"
                            />
                            Valid
                          </label>
                        </div>
                        {(occ.urls || []).map((urlRef, urlIndex) => (
                          <UrlRefRow
                            key={`${occ._key ?? idx}-url-${urlIndex}`}
                            urlRef={urlRef}
                            size="md"
                            {...urlRefRowHandlers(occ.urls || [], urlIndex, (urls) =>
                              setEditData((d) => ({
                                ...d,
                                occurrences: d.occurrences.map((o, i) => (i === idx ? { ...o, urls } : o)),
                              }))
                            )}
                          />
                        ))}
                        <OccurrenceTimesEditor
                          occ={occ}
                          artists={selectedEvent?.artistsData || []}
                          hasSiblings={hasShowSiblings(occ, editData.occurrences)}
                          onChange={(patch) =>
                            setEditData((d) => ({
                              ...d,
                              occurrences: d.occurrences.map((o, i) => (i === idx ? { ...o, ...patch } : o)),
                            }))
                          }
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...editData.occurrences];
                            updated[idx] = {
                              ...updated[idx],
                              urls: [...(updated[idx].urls || []), { provider: "", url: "" }],
                            };
                            setEditData((d) => ({ ...d, occurrences: updated }));
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Add occurrence URL
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setEditData((d) => ({
                          ...d,
                          occurrences: [...(d.occurrences || []), makeEmptyOccurrence()],
                        }))
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add occurrence
                    </Button>
                  </div>
                )}
              </div>

              {/* Images Section */}
              <ImageListEditor
                value={normalizeEventImages(editData.images).map((image) => image.url)}
                onChange={handleEventImagesChange}
                activeUrl={editData.primaryImageUrl || selectedEvent?.primaryImageUrl || selectedEvent?.image}
                mobileActiveUrl={editData.mobileImageUrl || selectedEvent?.mobileImageUrl || selectedEvent?.primaryImageUrl || selectedEvent?.image}
                desktopAutomatic={!editData.primaryImageUrl}
                mobileAutomatic={!editData.mobileImageUrl}
                onSelectDesktop={(url) => setEditData((data) => ({ ...data, primaryImageUrl: url }))}
                onSelectMobile={(url) => setEditData((data) => ({ ...data, mobileImageUrl: url }))}
                onUseAutomaticDesktop={() => setEditData((data) => ({ ...data, primaryImageUrl: null }))}
                onUseAutomaticMobile={() => setEditData((data) => ({ ...data, mobileImageUrl: null }))}
                suspiciousUrls={editSuspiciousImages}
                renderBadge={(url) => {
                  const source = normalizeEventImages(editData.images).find((image) => image.url === url)?.source;
                  return source ? <Badge variant="secondary" className="text-[10px]">{source}</Badge> : null;
                }}
              />

              <ModalFooter>
                <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setSelectedEvent(null); }} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Save</>
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Manual duplicate: flag another event as a duplicate of the open (canonical) one */}
      <Modal open={isMarkDuplicateOpen} onOpenChange={(open) => { if (!dupBusy) setIsMarkDuplicateOpen(open); }}>
        <ModalContent title="Add duplicate" className="max-w-lg">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Canonical: <span className="font-medium text-foreground">{displayLabel(selectedEvent?.preferredTitle, selectedEvent?.title)}</span>.
              The event you pick is flagged as its duplicate.
            </p>

            <EntitySearchPicker
              id="mark_duplicate_event_search"
              label="Duplicate event"
              required
              placeholder="Search active events by title, or paste an event ID..."
              value={dupPicked}
              onChange={setDupPicked}
              disabled={dupBusy}
              excludeId={selectedEvent?.id}
              allowRawId={false}
              emptyLabel="No active event matches that title or ID."
              search={(query, config) =>
                eventsApi
                  .getAll({ search: query, status: "active", page_size: 8 }, config)
                  .then((data) => data.events || [])
              }
              optionLabel={(event) => displayLabel(event.preferred_title, event.title)}
              optionSublabel={(event) =>
                [
                  displayLabel(event.venue?.preferred_name, event.venue?.name) || event.venue_name,
                  formatDate(event.occurrences?.[0]?.local_date),
                ]
                  .filter((part) => part && part !== "-")
                  .join(" · ")
              }
            />

            <OnlyMarkDuplicateToggle checked={dupOnlyMark} onChange={setDupOnlyMark} disabled={dupBusy} />

            {dupError && <p className="text-sm text-destructive">{dupError}</p>}
          </div>

          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsMarkDuplicateOpen(false)} disabled={dupBusy}>
              Cancel
            </Button>
            <Button onClick={handleMarkDuplicate} disabled={!dupPicked || dupBusy}>
              {dupBusy ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Marking...</>
              ) : (
                <><GitMerge className="h-4 w-4 mr-2" />Mark as duplicate</>
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete Event">
          <p className="text-muted-foreground">
            Are you sure you want to delete "{displayLabel(selectedEvent?.preferredTitle, selectedEvent?.title)}"? This action cannot be undone.
          </p>
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteModalOpen(false); setSelectedEvent(null); }} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                "Delete"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
